(function () {
  const kqvCartFetchPatch = (() => {
    if (window.__kqvCartFetchPatchApi) {
      return window.__kqvCartFetchPatchApi;
    }
    const contextsByVariant = new Map();
    const cartTransformers = [];
    const originalFetch = window.fetch.bind(window);
    const getUrl = (input) => {
      if (typeof input === 'string') return input;
      return input?.url || '';
    };
    const isCartAddUrl = (url) =>
      String(url).includes('/cart/add');
    const getPropertiesFromFormData = (formData) => {
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(
          /^properties\\[(.+)]$/
        );
        if (!match || value === '') continue;
        properties[match[1]] = value;
      }
      return properties;
    };
    const createJsonHeaders = (headersInit) => {
      const headers = new Headers(
        headersInit || {}
      );
      headers.set(
        'Content-Type',
        'application/json'
      );
      headers.set(
        'Accept',
        'application/json'
      );
      headers.set(
        'X-Requested-With',
        'XMLHttpRequest'
      );
      return headers;
    };
    /*
     * Shopify retourne un objet avec items[]
     * lorsque l'ajout utilise le format JSON.
     *
     * Dawn attend cependant les données de la ligne
     * ajoutée à la racine de la réponse.
     *
     * On normalise donc la réponse avant qu'elle
     * ne soit lue par product-form.js.
     */
    const normalizeResponseForDawn = async (
      response,
      fallbackVariantId
    ) => {
      let data;
      try {
        data = await response.clone().json();
      } catch (error) {
        return response;
      }
      const firstItem =
        Array.isArray(data?.items) &&
        data.items.length
          ? data.items[0]
          : null;
      if (!firstItem) {
        return response;
      }
      const normalizedData = {
        ...firstItem,
        ...data,
        id:
          firstItem.id ||
          firstItem.variant_id ||
          fallbackVariantId,
      };
      const headers = new Headers(
        response.headers
      );
      headers.delete('content-length');
      headers.delete('content-encoding');
      headers.delete('transfer-encoding');
      return new Response(
        JSON.stringify(normalizedData),
        {
          status: response.status,
          statusText: response.statusText,
          headers,
        }
      );
    };
    const register = (
      variantId,
      context
    ) => {
      if (!variantId || !context) return;
      const key = String(variantId);
      const contexts =
        contextsByVariant.get(key) || [];
      contexts.push(context);
      contextsByVariant.set(
        key,
        contexts
      );
    };
    const registerCartTransformer = (
      transformer
    ) => {
      if (
        typeof transformer !== 'function' ||
        cartTransformers.includes(transformer)
      ) {
        return;
      }
      cartTransformers.push(transformer);
    };
    const findActiveContext = (
      variantId
    ) => {
      const contexts =
        contextsByVariant.get(
          String(variantId)
        ) || [];
      return (
        contexts.find(
          (context) =>
            context.isActive?.() === true &&
            context.getPurchaseType?.() ===
              'subscription' &&
            context.isSubscriptionAvailable?.() ===
              true
        ) || null
      );
    };
    const buildPayloadFromFormData = (
      formData
    ) => {
      const variantId = Number(
        formData.get('id')
      );
      if (!Number.isFinite(variantId)) {
        return null;
      }
      const rawQuantity = Number(
        formData.get('quantity') || 1
      );
      const quantity =
        Number.isFinite(rawQuantity) &&
        rawQuantity > 0
          ? rawQuantity
          : 1;
      const item = {
        id: variantId,
        quantity,
      };
      const properties =
        getPropertiesFromFormData(
          formData
        );
      if (
        Object.keys(properties).length
      ) {
        item.properties = properties;
      }
      const payload = {
        items: [item],
      };
      const sections =
        formData.get('sections');
      const sectionsUrl =
        formData.get('sections_url');
      if (sections) {
        payload.sections = sections;
      }
      if (sectionsUrl) {
        payload.sections_url =
          sectionsUrl;
      }
      return {
        payload,
        variantId,
      };
    };
    const buildSubscriptionPayloadFromFormData = (
      formData,
      context
    ) => {
      const base =
        buildPayloadFromFormData(
          formData
        );
      if (!base) return null;
      const sellingPlanId = Number(
        context.getSellingPlanId?.()
      );
      if (
        !Number.isFinite(sellingPlanId)
      ) {
        return null;
      }
      base.payload.items[0].selling_plan =
        sellingPlanId;

      // One quarterly order must contain three monthly packs.
      // The selling plan stays attached to the same variant,
      // so Shopify will also renew this quantity.
      const multiplier = Math.max(
        1,
        Number(
          context.getCartQuantityMultiplier?.() || 1
        ) || 1
      );
      base.payload.items[0].quantity *= multiplier;
      return base;
    };
    const applyCartTransformers = (
      payload,
      variantId
    ) => {
      let currentPayload = payload;
      let currentVariantId = variantId;
      let changed = false;
      cartTransformers.forEach(
        (transformer) => {
          let result = null;
          try {
            result = transformer({
              payload: currentPayload,
              variantId:
                currentVariantId,
            });
          } catch (error) {
            console.error(
              '[KQV] cart transformer error',
              error
            );
          }
          if (!result) return;
          if (result.payload) {
            currentPayload =
              result.payload;
          }
          if (
            result.variantId !==
              undefined &&
            result.variantId !== null &&
            result.variantId !== ''
          ) {
            currentVariantId =
              result.variantId;
          }
          changed = true;
        }
      );
      return {
        payload: currentPayload,
        variantId:
          currentVariantId,
        changed,
      };
    };
    /*
     * Si une autre couche du thème envoie déjà
     * du JSON, on injecte selling_plan si besoin,
     * puis on laisse les composants enregistrés
     * compléter le payload (upsells, gifts, etc.).
     */
    const patchJsonPayload = (
      rawBody
    ) => {
      let payload;
      try {
        payload = JSON.parse(rawBody);
      } catch (error) {
        return null;
      }
      if (
        !Array.isArray(payload?.items) ||
        !payload.items.length
      ) {
        return null;
      }
      let changed = false;
      let variantId = Number(
        payload.items[0]?.id || 0
      );
      const matchedItem =
        payload.items.find((item) => {
          const context =
            findActiveContext(item?.id);
          return Boolean(context);
        });
      if (matchedItem) {
        const context =
          findActiveContext(
            matchedItem.id
          );
        const sellingPlanId = Number(
          context?.getSellingPlanId?.()
        );
        if (
          Number.isFinite(
            sellingPlanId
          )
        ) {
          matchedItem.selling_plan =
            sellingPlanId;

          // Match the quantity used by the FormData route.
          // Only the selected product line is multiplied;
          // any gifts / upsells keep their own quantities.
          const rawQuantity = Number(
            matchedItem.quantity || 1
          );
          const multiplier = Math.max(
            1,
            Number(
              context.getCartQuantityMultiplier?.() || 1
            ) || 1
          );
          matchedItem.quantity =
            (Number.isFinite(rawQuantity) && rawQuantity > 0
              ? rawQuantity
              : 1) * multiplier;

          variantId = Number(
            matchedItem.id
          );
          changed = true;
        }
      }
      const extra =
        applyCartTransformers(
          payload,
          variantId
        );
      if (extra.changed) {
        payload = extra.payload;
        variantId = extra.variantId;
        changed = true;
      }
      if (!changed) {
        return null;
      }
      return {
        payload,
        variantId,
      };
    };
    window.fetch = async function (
      input,
      init = {}
    ) {
      const url = getUrl(input);
      if (!isCartAddUrl(url)) {
        return originalFetch(
          input,
          init
        );
      }
      let transformed = null;
      if (
        typeof FormData !==
          'undefined' &&
        init.body instanceof FormData
      ) {
        const variantId = String(
          init.body.get('id') || ''
        );
        const context =
          findActiveContext(
            variantId
          );
        let base = null;
        if (context) {
          base =
            buildSubscriptionPayloadFromFormData(
              init.body,
              context
            );
        } else if (
          cartTransformers.length
        ) {
          base =
            buildPayloadFromFormData(
              init.body
            );
        }
        if (base) {
          const extra =
            applyCartTransformers(
              base.payload,
              base.variantId
            );
          if (
            context ||
            extra.changed
          ) {
            transformed = {
              payload:
                extra.payload,
              variantId:
                extra.variantId,
            };
          }
        }
      } else if (
        typeof init.body ===
          'string' &&
        init.body
          .trim()
          .startsWith('{')
      ) {
        transformed =
          patchJsonPayload(
            init.body
          );
      }
      /*
       * Requête qui ne vient pas du picker / upsell :
       * aucun changement.
       */
      if (!transformed) {
        return originalFetch(
          input,
          init
        );
      }
      const response =
        await originalFetch(input, {
          ...init,
          headers:
            createJsonHeaders(
              init.headers
            ),
          body: JSON.stringify(
            transformed.payload
          ),
        });
      return normalizeResponseForDawn(
        response,
        transformed.variantId
      );
    };
    const api = {
      register,
      registerCartTransformer,
    };
    window.__kqvCartFetchPatchApi =
      api;
    return api;
  })();
  function initKaijuQuantityVariantPickers(
    root = document
  ) {
    const pickers = Array.from(
      root.querySelectorAll('.kqv')
    );
    if (!pickers.length) return;
    pickers.forEach((picker) => {
      if (
        picker.dataset
          .kqvInitialized === 'true'
      ) {
        return;
      }
      const cards = Array.from(
        picker.querySelectorAll(
          '[data-kqv-card]'
        )
      );
      if (!cards.length) return;
      picker.dataset.kqvInitialized =
        'true';
      const formId =
        picker.dataset.productFormId;
      const isSizeQuantityMode =
        picker.dataset.kqvMode ===
        'size-quantity';
      const hasSubscription =
        picker.dataset
          .kqvHasSubscription ===
        'true';
      const legacySellingPlanId = String(
        picker.dataset
          .sellingPlanId || ''
      ).trim();
      const hasValidLegacySellingPlanId =
        /^\d+$/.test(
          legacySellingPlanId
        );
      const renewalText =
        picker.dataset
          .subscriptionRenewalText ||
        'Renews every 28 days';
      const purchaseOptions =
        Array.from(
          picker.querySelectorAll(
            '[data-kqv-purchase-option]'
          )
        );
      const subscriptionDetails =
        picker.querySelector(
          '[data-kqv-subscription-details]'
        );
      const planOptionsContainer =
        picker.querySelector(
          '[data-kqv-plan-options]'
        );
      const subscriptionPerServing =
        picker.querySelector(
          '[data-kqv-subscription-per-serving]'
        );
      const subscriptionDiscountBenefit =
        picker.querySelector(
          '[data-kqv-subscription-discount-benefit]'
        );
      const useEnhancedSubscriptionUI =
        !isSizeQuantityMode &&
        Boolean(subscriptionDetails);
      const isFrench =
        String(
          document.documentElement.lang ||
          ''
        )
          .toLowerCase()
          .startsWith('fr');
      const subscriptionCopy =
        isFrench
          ? {
              everyMonth: 'Tous les mois',
              everyMonths: (count) =>
                `Tous les ${count} mois`,
              monthFree: '1 mois offert',
              monthsFree: (count) =>
                `${count} mois offerts`,
              monthSuffix: '/ mois',
              perServingSuffix: '/ boisson',
              discountEveryRenewal: (discount) =>
                `${discount}% de réduction à chaque renouvellement`,
              monthsSuffix: (count) =>
                `/ ${count} mois`,
              freeBottle: 'Bouteille Kaiju offerte',
              priceLocked:
                'Prix bloqué à chaque renouvellement',
              cancelAnytime:
                'Reporter ou annuler en un clic',
              unavailableSubscription:
                'Disponible avec 3 ou 6 boîtes',
              forMonths: (count) =>
                `pour ${count} mois`,
            }
          : {
              everyMonth: 'Every month',
              everyMonths: (count) =>
                `Every ${count} months`,
              monthFree: '1 month free',
              monthsFree: (count) =>
                `${count} months free`,
              monthSuffix: '/ month',
              perServingSuffix: '/ drink',
              discountEveryRenewal: (discount) =>
                `${discount}% off every renewal`,
              monthsSuffix: (count) =>
                `/ ${count} months`,
              freeBottle: 'Free Kaiju bottle',
              priceLocked:
                'Price locked on every renewal',
              cancelAnytime:
                'Push back or cancel in one click',
              unavailableSubscription:
                'Available on 3 or 6 boxes',
              forMonths: (count) =>
                `for ${count} months`,
            };
      let activePurchaseType =
        'one-time';
      let activeSellingPlanId = '';
      const productId = String(
        picker.dataset.productId || ''
      );
      window.__kqvSellingPlanPreferences =
        window.__kqvSellingPlanPreferences ||
        new Map();
      const sellingPlanPreferences =
        window.__kqvSellingPlanPreferences;
      const getSellingPlanPreferenceKey = (
        card
      ) => {
        if (!card) return '';
        return `${productId}:${card.dataset.variantId || ''}`;
      };
      const getStoredSellingPlanId = (
        card
      ) => {
        const key =
          getSellingPlanPreferenceKey(
            card
          );
        return key
          ? String(
              sellingPlanPreferences.get(
                key
              ) || ''
            )
          : '';
      };
      const storeSellingPlanId = (
        card,
        planId
      ) => {
        const key =
          getSellingPlanPreferenceKey(
            card
          );
        const id = String(
          planId || ''
        ).trim();
        if (!key || !id) return;
        sellingPlanPreferences.set(
          key,
          id
        );
      };
      /* =====================================================
         FORM
         ===================================================== */
      const resolveFormElement = (
        element
      ) => {
        if (!element) return null;
        if (
          element.tagName === 'FORM'
        ) {
          return element;
        }
        return (
          element.querySelector?.(
            'form'
          ) || null
        );
      };
      const getForm = () => {
        if (formId) {
          const exactForm =
            resolveFormElement(
              document.getElementById(
                formId
              )
            );
          if (exactForm) {
            return exactForm;
          }
        }
        return (
          picker.closest('form') ||
          picker
            .closest('product-form')
            ?.querySelector('form') ||
          null
        );
      };
      const getFormElements = () => {
        const form = getForm();
        return form?.elements
          ? Array.from(form.elements)
          : [];
      };
      const getVariantInputs = () => {
        return getFormElements().filter(
          (element) =>
            element.name === 'id' ||
            element.classList?.contains(
              'product-variant-id'
            )
        );
      };
      const getSubmitButton = () => {
        const form = getForm();
        return (
          getFormElements().find(
            (element) =>
              element.name === 'add'
          ) ||
          form?.querySelector(
            '[name="add"]'
          ) ||
          null
        );
      };
      const getAtcLabel = () => {
        return (
          getForm()?.querySelector(
            '.atc-label'
          ) || null
        );
      };
      const getAtcDiscount = () => {
        return (
          getForm()?.querySelector(
            '.qty-test-atc__discount'
          ) || null
        );
      };
      const getDynamicCheckout = () => {
        return (
          getForm()?.querySelector(
            '.shopify-payment-button'
          ) || null
        );
      };
      const getSelectedCard = () => {
        return (
          cards.find((card) =>
            card.classList.contains(
              'is-selected'
            )
          ) || null
        );
      };
      const addToCartLabel =
        picker.dataset
          .labelAddToCart ||
        'Add to cart';
      const soldOutLabel =
        picker.dataset.labelSoldOut ||
        'Sold out';
      /* =====================================================
         PACKS / DISCOUNTS
         ===================================================== */
      const getPackCount = (
        card
      ) => {
        if (!card) return 1;
        const rawValue =
          card.dataset.packKey ||
          card.querySelector(
            '.kqv__pack-title'
          )?.textContent ||
          '';
        const match = String(
          rawValue
        ).match(
          /\d+(?:[.,]\d+)?/
        );
        if (!match) return 1;
        const count = Number(
          match[0].replace(',', '.')
        );
        return Number.isFinite(
          count
        )
          ? count
          : 1;
      };
      const getSinglePackCard = () => {
        if (isSizeQuantityMode) {
          return null;
        }
        return (
          cards.find(
            (card) =>
              getPackCount(card) === 1
          ) || null
        );
      };
      /*
       * Legacy helpers kept for Gel / compatibility.
       * Main ATC badges now use the real price difference
       * calculated inside getPricingForCard().
       */
      const getPackDiscount = (
        card
      ) => {
        const packCount =
          getPackCount(card);
        if (!isSizeQuantityMode) {
          if (packCount === 3) {
            return 15;
          }
          if (packCount === 6) {
            return 25;
          }
        }
        const liquidDiscount =
          Number(
            card?.dataset.discount ||
              0
          );
        return liquidDiscount > 0
          ? liquidDiscount
          : 0;
      };
      const getSubscriptionDiscount = (
        card,
        plan = null
      ) => {
        if (!card) return 0;
        const oneTimePrice = Number(
          card.dataset.price || 0
        );
        const selectedPlan =
          plan ||
          getActiveSellingPlanForCard(
            card
          );
        const subscriptionPrice =
          Number(
            selectedPlan?.deliveryPrice ||
            card.dataset
              .subscriptionPrice ||
            0
          );
        if (
          oneTimePrice > 0 &&
          subscriptionPrice > 0 &&
          subscriptionPrice <
            oneTimePrice
        ) {
          return (
            ((oneTimePrice -
              subscriptionPrice) /
              oneTimePrice) *
            100
          );
        }
        const liquidDiscount =
          Number(
            card.dataset
              .subscriptionDiscount ||
              0
          );
        return liquidDiscount > 0
          ? liquidDiscount
          : 0;
      };
      const getTotalSubscriptionDiscount = (
        card
      ) => {
        const packDiscount =
          getPackDiscount(card);
        const subscriptionDiscount =
          getSubscriptionDiscount(card);
        return (
          100 -
          ((100 - packDiscount) *
            (100 -
              subscriptionDiscount)) /
            100
        );
      };
      const roundPercent = (
        value
      ) => {
        return (
          Math.round(
            (Number(value) +
              Number.EPSILON) *
              10
          ) / 10
        );
      };
      const formatPercent = (
        value
      ) => {
        const rounded =
          roundPercent(value);
        try {
          return new Intl.NumberFormat(
            document.documentElement
              .lang || undefined,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            }
          ).format(rounded);
        } catch (error) {
          return Number.isInteger(
            rounded
          )
            ? String(rounded)
            : rounded.toFixed(1);
        }
      };
      /* =====================================================
         MONEY / COMPARE PRICE
         ===================================================== */
      const formatMoneyLike = (
        cents,
        sampleFormatted
      ) => {
        const amount = Number(cents);
        if (
          !Number.isFinite(amount)
        ) {
          return '';
        }
        const sample = String(
          sampleFormatted || ''
        );
        const decimalSeparator =
          sample.includes(',')
            ? ','
            : '.';
        const hasDecimals =
          Math.abs(amount % 100) > 0;
        let number = (
          amount / 100
        ).toFixed(
          hasDecimals ? 2 : 0
        );
        if (
          decimalSeparator === ','
        ) {
          number = number.replace(
            '.',
            ','
          );
        }
        const symbol = sample
          .replace(
            /[0-9.,\s\u00a0-]/g,
            ''
          )
          .trim();
        if (!symbol) {
          return number;
        }
        const firstDigitIndex =
          sample.search(/\d/);
        const firstSymbolIndex =
          sample.search(
            /[^\d.,\s\u00a0-]/
          );
        const symbolBefore =
          firstSymbolIndex !== -1 &&
          firstDigitIndex !== -1 &&
          firstSymbolIndex <
            firstDigitIndex;
        return symbolBefore
          ? `${symbol}${number}`
          : `${number}${symbol}`;
      };
      const getReferencePrice = (
        card
      ) => {
        if (!card) {
          return {
            cents: 0,
            formatted: '',
          };
        }
        const currentPrice = Number(
          card.dataset.price || 0
        );
        const currentPriceFormatted =
          card.dataset
            .priceFormatted || '';
        /*
         * Quantity products:
         * reference = 1-box price × quantity.
         */
        if (!isSizeQuantityMode) {
          const packCount =
            getPackCount(card);
          const singlePackPrice =
            Number(
              getSinglePackCard()
                ?.dataset.price || 0
            );
          if (
            packCount > 1 &&
            singlePackPrice > 0
          ) {
            const referencePrice =
              singlePackPrice *
              packCount;
            return {
              cents: referencePrice,
              formatted:
                formatMoneyLike(
                  referencePrice,
                  currentPriceFormatted
                ),
            };
          }
        }
        const compareAtPrice =
          Number(
            card.dataset
              .compareAtPrice || 0
          );
        if (
          compareAtPrice >
          currentPrice
        ) {
          return {
            cents: compareAtPrice,
            formatted:
              card.dataset
                .compareAtPriceFormatted ||
              formatMoneyLike(
                compareAtPrice,
                currentPriceFormatted
              ),
          };
        }
        return {
          cents: 0,
          formatted: '',
        };
      };
      const parsePlanIntervalMonths = (
        value
      ) => {
        const text = String(
          value || ''
        ).trim();
        if (!text) return null;

        const explicitMonths = text.match(
          /\b(\d+)\s*[- ]?\s*(?:months?|mois)\b/i
        );
        if (explicitMonths) {
          const count = Number(
            explicitMonths[1]
          );
          if (
            Number.isFinite(count) &&
            count > 0
          ) {
            return count;
          }
        }

        if (
          /\b(?:month|monthly|every\s+month|mois|mensuel(?:le)?|chaque\s+mois)\b/i.test(
            text
          )
        ) {
          return 1;
        }

        if (
          /\b(?:quarterly|trimestriel(?:le)?)\b/i.test(
            text
          )
        ) {
          return 3;
        }

        return null;
      };

      const getPlanIntervalMonths = (
        plan
      ) => {
        if (!plan) return 1;

        const options = Array.isArray(
          plan.options
        )
          ? plan.options
          : [];

        const parsedOptions = options
          .map((option) => ({
            ...option,
            intervalMonths:
              parsePlanIntervalMonths(
                option?.value
              ),
          }))
          .filter(
            (option) =>
              Number.isFinite(
                option.intervalMonths
              ) &&
              option.intervalMonths > 0
          );

        /*
         * Shopify exposes selling-plan options separately
         * from the marketing / checkout plan name. Prefer
         * an explicitly delivery-related option when there
         * are several (for example delivery + billing).
         */
        const deliveryOption =
          parsedOptions.find((option) =>
            /\b(?:delivery|deliver|fulfil|fulfill|shipping|livraison|exp[eé]dition)\b/i.test(
              String(option?.name || '')
            )
          );

        if (deliveryOption) {
          return deliveryOption.intervalMonths;
        }

        if (parsedOptions.length) {
          const intervals = [
            ...new Set(
              parsedOptions.map(
                (option) =>
                  option.intervalMonths
              )
            ),
          ];

          if (intervals.length === 1) {
            return intervals[0];
          }

          /*
           * When delivery and billing cadences differ, the
           * delivery interval is normally the shorter one.
           * This also keeps historical prepaid plans sane
           * without depending on their plan name.
           */
          return Math.min(...intervals);
        }

        /*
         * Compatibility fallback for legacy allocations
         * that expose no selling_plan.options in Liquid.
         * No plan-name parsing is used here.
         */
        return Math.max(
          1,
          Number(plan.deliveryCount || 1) || 1
        );
      };
      // Applies to box pickers, not to Gel / size-quantity.
      // The quarterly pay-as-you-go unit price represents one
      // month's supply (3 Boxes or 6 Boxes). We ship 3 of
      // those variants per quarterly order. Prepaid plans
      // already include multiple deliveries and are excluded.
      const getCartQuantityMultiplier = (
        plan
      ) => {
        if (
          !useEnhancedSubscriptionUI ||
          !plan ||
          plan.deliveryCount > 1
        ) {
          return 1;
        }
        return getPlanIntervalMonths(plan) === 3
          ? 3
          : 1;
      };

      const getPlanPriceSuffix = (
        plan
      ) => {
        if (!plan) {
          return subscriptionCopy.monthSuffix;
        }
        /*
         * Historical prepaid plans expose a per-delivery
         * price and several deliveries in one billing period.
         * Keep their historical / month display.
         *
         * Pay-as-you-go multi-month plans have one delivery
         * per billing period, so show the real renewal cadence.
         */
        if (
          plan.deliveryCount > 1 ||
          getCartQuantityMultiplier(plan) > 1
        ) {
          return subscriptionCopy.monthSuffix;
        }
        const intervalMonths =
          getPlanIntervalMonths(plan);
        return intervalMonths > 1
          ? subscriptionCopy.monthsSuffix(
              intervalMonths
            )
          : subscriptionCopy.monthSuffix;
      };
      const getSellingPlansForCard = (
        card
      ) => {
        if (!card) return [];
        const nodes = Array.from(
          card.querySelectorAll(
            '[data-kqv-selling-plan][data-selling-plan-id]'
          )
        );
        const plans = nodes
          .map((node) => {
            const id = String(
              node.dataset
                .sellingPlanId || ''
            ).trim();
            if (!/^\d+$/.test(id)) {
              return null;
            }
            const deliveryCount =
              Math.max(
                1,
                Number(
                  node.dataset
                    .sellingPlanDeliveryCount ||
                  1
                ) || 1
              );
            const options = Array.from(
              node.querySelectorAll(
                '[data-kqv-selling-plan-option]'
              )
            ).map((optionNode) => ({
              name:
                optionNode.dataset
                  .sellingPlanOptionName ||
                '',
              value:
                optionNode.dataset
                  .sellingPlanOptionValue ||
                '',
              position: Number(
                optionNode.dataset
                  .sellingPlanOptionPosition ||
                0
              ),
            }));
            return {
              id,
              name:
                node.dataset
                  .sellingPlanName ||
                '',
              options,
              totalPrice: Number(
                node.dataset
                  .sellingPlanTotalPrice ||
                0
              ),
              totalPriceFormatted:
                node.dataset
                  .sellingPlanTotalPriceFormatted ||
                '',
              deliveryPrice: Number(
                node.dataset
                  .sellingPlanDeliveryPrice ||
                node.dataset
                  .sellingPlanTotalPrice ||
                0
              ),
              deliveryPriceFormatted:
                node.dataset
                  .sellingPlanDeliveryPriceFormatted ||
                node.dataset
                  .sellingPlanTotalPriceFormatted ||
                '',
              deliveryCount,
              perServingPriceFormatted:
                node.dataset
                  .sellingPlanPerServingPriceFormatted ||
                '',
              source: 'allocation',
            };
          })
          .filter(Boolean);
        if (plans.length) {
          return plans;
        }
        /*
         * Legacy fallback for Gel / old products that
         * still expose only the historical monthly plan
         * through card data attributes.
         */
        if (
          hasValidLegacySellingPlanId &&
          card.dataset
            .subscriptionAvailable ===
            'true'
        ) {
          return [
            {
              id: legacySellingPlanId,
              name: 'Monthly Subscription',
              totalPrice: Number(
                card.dataset
                  .subscriptionPrice ||
                card.dataset.price ||
                0
              ),
              totalPriceFormatted:
                card.dataset
                  .subscriptionPriceFormatted ||
                card.dataset
                  .priceFormatted ||
                '',
              deliveryPrice: Number(
                card.dataset
                  .subscriptionPrice ||
                card.dataset.price ||
                0
              ),
              deliveryPriceFormatted:
                card.dataset
                  .subscriptionPriceFormatted ||
                card.dataset
                  .priceFormatted ||
                '',
              deliveryCount: 1,
              options: [],
              perServingPriceFormatted:
                card.dataset
                  .subscriptionPerServingPriceFormatted ||
                card.dataset
                  .perServingPriceFormatted ||
                '',
              source: 'legacy',
            },
          ];
        }
        return [];
      };
      const getPreferredSellingPlan = (
        card
      ) => {
        const plans =
          getSellingPlansForCard(card);
        if (!plans.length) {
          return null;
        }
        const storedPlanId =
          getStoredSellingPlanId(
            card
          );
        const storedPlan =
          storedPlanId
            ? plans.find(
                (plan) =>
                  plan.id ===
                  storedPlanId
              )
            : null;
        if (storedPlan) {
          return storedPlan;
        }
        const activePlan = plans.find(
          (plan) =>
            plan.id ===
            activeSellingPlanId
        );
        if (activePlan) {
          return activePlan;
        }
        return plans
          .slice()
          .sort((a, b) => {
            const intervalDifference =
              getPlanIntervalMonths(b) -
              getPlanIntervalMonths(a);
            if (intervalDifference !== 0) {
              return intervalDifference;
            }
            return (
              b.deliveryCount -
              a.deliveryCount
            );
          })[0];
      };
      const getActiveSellingPlanForCard = (
        card
      ) => {
        return getPreferredSellingPlan(
          card
        );
      };
      const ensureActiveSellingPlanForCard = (
        card
      ) => {
        const plans =
          getSellingPlansForCard(card);
        const plan =
          getPreferredSellingPlan(card);
        if (plan) {
          activeSellingPlanId =
            plan.id;
          if (
            plans.length > 1 &&
            !getStoredSellingPlanId(
              card
            )
          ) {
            storeSellingPlanId(
              card,
              plan.id
            );
          }
        }
        return plan;
      };
      const getPlanDiscount = (
        card,
        plan
      ) => {
        if (!card || !plan) return 0;
        const oneTimePrice = Number(
          card.dataset.price || 0
        );
        const deliveryPrice = Number(
          plan.deliveryPrice || 0
        );
        if (
          oneTimePrice <= 0 ||
          deliveryPrice <= 0 ||
          deliveryPrice >= oneTimePrice
        ) {
          return 0;
        }
        return (
          ((oneTimePrice -
            deliveryPrice) /
            oneTimePrice) *
          100
        );
      };
      const getPlanComparePrice = (
        card,
        plan
      ) => {
        if (!card || !plan) {
          return {
            cents: 0,
            formatted: '',
          };
        }
        const plans =
          getSellingPlansForCard(card);
        if (
          plan.deliveryCount > 1
        ) {
          const monthlyPlan =
            plans.find(
              (candidate) =>
                candidate.deliveryCount === 1 &&
                candidate.deliveryPrice >
                  plan.deliveryPrice
            );
          if (monthlyPlan) {
            return {
              cents:
                monthlyPlan.deliveryPrice,
              formatted:
                monthlyPlan
                  .deliveryPriceFormatted,
            };
          }
        }
        const oneTimePrice = Number(
          card.dataset.price || 0
        );
        if (
          oneTimePrice >
          plan.deliveryPrice
        ) {
          return {
            cents: oneTimePrice,
            formatted:
              card.dataset
                .priceFormatted ||
              formatMoneyLike(
                oneTimePrice,
                plan.deliveryPriceFormatted
              ),
          };
        }
        return {
          cents: 0,
          formatted: '',
        };
      };
      const getPlanFreeMonths = (
        card,
        plan
      ) => {
        if (!card || !plan) {
          return 0;
        }
        const oneTimePrice = Number(
          card.dataset.price || 0
        );
        const totalPrice = Number(
          plan.totalPrice || 0
        );
        if (
          oneTimePrice <= 0 ||
          totalPrice <= 0
        ) {
          return 0;
        }
        const isPrepaid =
          plan.deliveryCount > 1;
        const coverageMonths = isPrepaid
          ? plan.deliveryCount
          : getPlanIntervalMonths(plan);
        if (coverageMonths <= 1) {
          return 0;
        }
        const referencePrice = isPrepaid
          ? oneTimePrice * plan.deliveryCount
          : oneTimePrice;
        if (
          referencePrice <= 0 ||
          totalPrice >= referencePrice
        ) {
          return 0;
        }
        const discountRate =
          (referencePrice - totalPrice) /
          referencePrice;
        const freeMonths =
          coverageMonths * discountRate;
        if (freeMonths < 0.5) {
          return 0;
        }
        return Math.max(
          1,
          Math.round(freeMonths)
        );
      };
      const getPlanLabel = (
        plan,
        card
      ) => {
        if (!plan) return '';
        const intervalMonths =
          getPlanIntervalMonths(plan);
        const label = intervalMonths > 1
          ? subscriptionCopy.everyMonths(
              intervalMonths
            )
          : subscriptionCopy.everyMonth;
        const multiplier =
          getCartQuantityMultiplier(plan);
        if (multiplier === 1 || !card) {
          return label;
        }
        const totalBoxes =
          getPackCount(card) * multiplier;
        return `${label} · ${totalBoxes} ${
          isFrench ? 'boîtes' : 'boxes'
        }`;
      };
      const isSubscriptionAvailableForCard = (
        card
      ) => {
        if (
          !hasSubscription ||
          !card ||
          card.dataset.available !==
            'true'
        ) {
          return false;
        }
        return (
          getSellingPlansForCard(
            card
          ).length > 0
        );
      };
      /*
       * Pricing used by the custom ATC.
       *
       * One-time keeps the historical pack reference.
       * Subscription uses the selected selling plan total.
       * For prepaid plans the reference is the same pack
       * bought one-time for each prepaid delivery.
       */
      const getPricingForCard = (
        card
      ) => {
        const useSubscription =
          activePurchaseType ===
            'subscription' &&
          isSubscriptionAvailableForCard(
            card
          );
        if (useSubscription) {
          const plan =
            ensureActiveSellingPlanForCard(
              card
            );
          if (plan) {
            const multiplier =
              getCartQuantityMultiplier(plan);
            const actualPrice =
              Number(plan.totalPrice || 0) *
              multiplier;
            const oneTimePrice = Number(
              card.dataset.price || 0
            );
            const referencePrice =
              oneTimePrice *
              Math.max(
                1,
                plan.deliveryCount
              ) * multiplier;
            const discount =
              referencePrice >
                  actualPrice &&
              referencePrice > 0 &&
              actualPrice > 0
                ? Math.round(
                    (
                      (referencePrice -
                        actualPrice) /
                      referencePrice
                    ) *
                      100
                  )
                : 0;
            return {
              isSubscription: true,
              sellingPlan: plan,
              priceFormatted:
                multiplier === 1 &&
                plan.totalPriceFormatted
                  ? plan.totalPriceFormatted
                  : formatMoneyLike(
                      actualPrice,
                      plan.totalPriceFormatted ||
                        card.dataset.priceFormatted
                    ),
              referencePrice,
              referencePriceFormatted:
                formatMoneyLike(
                  referencePrice,
                  card.dataset
                    .priceFormatted
                ),
              discount,
              discountFormatted:
                String(discount),
            };
          }
        }
        const reference =
          getReferencePrice(card);
        const actualPrice = Number(
          card.dataset.price || 0
        );
        const priceFormatted =
          card.dataset
            .priceFormatted ||
          '';
        const discount =
          reference.cents >
              actualPrice &&
          reference.cents > 0 &&
          actualPrice > 0
            ? Math.round(
                (
                  (reference.cents -
                    actualPrice) /
                  reference.cents
                ) *
                  100
              )
            : 0;
        return {
          isSubscription: false,
          sellingPlan: null,
          priceFormatted,
          referencePrice:
            reference.cents,
          referencePriceFormatted:
            reference.formatted,
          discount,
          discountFormatted:
            String(discount),
        };
      };
      /* =====================================================
         SELLING PLAN INPUT
         ===================================================== */
      const getSellingPlanInputs = (
        form
      ) => {
        if (!form?.elements) {
          return [];
        }
        return Array.from(
          form.elements
        ).filter(
          (element) =>
            element.name ===
            'selling_plan'
        );
      };
      const ensureSellingPlanInput = (
        form
      ) => {
        let input =
          getSellingPlanInputs(
            form
          ).find(
            (element) =>
              element.dataset
                .kqvSellingPlan ===
              'true'
          );
        if (!input) {
          input =
            document.createElement(
              'input'
            );
          input.type = 'hidden';
          input.name =
            'selling_plan';
          input.dataset
            .kqvSellingPlan =
            'true';
          form.appendChild(input);
        }
        return input;
      };
      const syncSellingPlanInput = (
        card
      ) => {
        const form = getForm();
        if (!form || !card) return;
        const customInput =
          ensureSellingPlanInput(
            form
          );
        /*
         * Désactive les champs Appstle
         * concurrents.
         */
        getSellingPlanInputs(form)
          .filter(
            (input) =>
              input !== customInput
          )
          .forEach((input) => {
            input.disabled = true;
            if (
              input.type ===
                'radio' ||
              input.type ===
                'checkbox'
            ) {
              input.checked = false;
            }
          });
        const shouldSubscribe =
          activePurchaseType ===
            'subscription' &&
          isSubscriptionAvailableForCard(
            card
          );
        const selectedPlan =
          shouldSubscribe
            ? ensureActiveSellingPlanForCard(
                card
              )
            : null;
        const selectedPlanId =
          selectedPlan?.id || '';
        customInput.value =
          shouldSubscribe
            ? selectedPlanId
            : '';
        customInput.disabled =
          !shouldSubscribe;
        form.dataset
          .kqvPurchaseType =
          activePurchaseType;
        form.dataset
          .kqvSellingPlanId =
          shouldSubscribe
            ? selectedPlanId
            : '';
      };
      /*
       * Chaque variante enregistre son contexte.
       * Le patch fetch vérifiera ensuite quelle
       * variante est réellement sélectionnée.
       */
      cards.forEach((card) => {
        const variantId =
          card.dataset.variantId;
        if (!variantId) return;
        kqvCartFetchPatch.register(
          variantId,
          {
            getPurchaseType: () =>
              activePurchaseType,
            getSellingPlanId: () => {
              const selectedCard =
                getSelectedCard();
              return (
                getActiveSellingPlanForCard(
                  selectedCard
                )?.id || ''
              );
            },
            getCartQuantityMultiplier: () => {
              const selectedCard =
                getSelectedCard();
              return getCartQuantityMultiplier(
                getActiveSellingPlanForCard(
                  selectedCard
                )
              );
            },
            isActive: () => {
              const selectedCard =
                getSelectedCard();
              return (
                picker.isConnected &&
                selectedCard?.dataset
                  .variantId ===
                  variantId
              );
            },
            isSubscriptionAvailable:
              () => {
                const selectedCard =
                  getSelectedCard();
                return (
                  selectedCard?.dataset
                    .variantId ===
                    variantId &&
                  isSubscriptionAvailableForCard(
                    selectedCard
                  )
                );
              },
          }
        );
      });
      /* =====================================================
         UI
         ===================================================== */
      const updateEnhancedBenefitCopy = () => {
        if (!subscriptionDetails) return;
        const giftText =
          subscriptionDetails.querySelector(
            '.kqv__subscription-benefit--gift span:last-child'
          );
        const lockedText =
          subscriptionDetails.querySelector(
            '.kqv__subscription-benefit--locked span:last-child'
          );
        const cancelText =
          subscriptionDetails.querySelector(
            '.kqv__subscription-benefit--cancel span:last-child'
          );
        if (giftText) {
          giftText.textContent =
            subscriptionCopy.freeBottle;
        }
        if (lockedText) {
          lockedText.textContent =
            subscriptionCopy.priceLocked;
        }
        if (cancelText) {
          cancelText.textContent =
            subscriptionCopy.cancelAnytime;
        }
      };
      const renderPlanOptions = (
        card,
        selectedPlan
      ) => {
        if (!planOptionsContainer) {
          return;
        }
        const plans =
          getSellingPlansForCard(card);
        planOptionsContainer.replaceChildren();
        if (plans.length <= 1) {
          planOptionsContainer.hidden = true;
          return;
        }
        planOptionsContainer.hidden = false;
        plans.forEach((plan) => {
          const button =
            document.createElement(
              'button'
            );
          button.type = 'button';
          button.className =
            'kqv__plan-option';
          button.dataset
            .kqvPlanOption =
            'true';
          button.dataset
            .sellingPlanId =
            plan.id;
          button.setAttribute(
            'role',
            'radio'
          );
          const isSelected =
            selectedPlan?.id ===
            plan.id;
          button.classList.toggle(
            'is-selected',
            isSelected
          );
          button.setAttribute(
            'aria-checked',
            isSelected
              ? 'true'
              : 'false'
          );
          const label =
            document.createElement(
              'span'
            );
          label.className =
            'kqv__plan-option-label';
          label.textContent =
            getPlanLabel(plan, card);
          button.appendChild(label);
          const freeMonths =
            getPlanFreeMonths(
              card,
              plan
            );
          if (freeMonths > 0) {
            const badge =
              document.createElement(
                'span'
              );
            badge.className =
              'kqv__plan-option-badge';
            badge.textContent =
              freeMonths === 1
                ? subscriptionCopy
                    .monthFree
                : subscriptionCopy
                    .monthsFree(
                      freeMonths
                    );
            button.appendChild(
              badge
            );
          }
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              activeSellingPlanId =
                plan.id;
              const selectedCard =
                getSelectedCard();
              if (selectedCard) {
                storeSellingPlanId(
                  selectedCard,
                  plan.id
                );
              }
              if (!selectedCard) {
                return;
              }
              updateUI(
                selectedCard,
                {
                  allowDisabled: true,
                  syncVariant: false,
                }
              );
            }
          );
          planOptionsContainer
            .appendChild(button);
        });
      };
      const updateSubscriptionDetails = (
        card
      ) => {
        if (!subscriptionDetails) {
          return;
        }
        if (
          !useEnhancedSubscriptionUI ||
          activePurchaseType !==
            'subscription' ||
          !isSubscriptionAvailableForCard(
            card
          )
        ) {
          subscriptionDetails.hidden =
            true;
          return;
        }
        const selectedPlan =
          ensureActiveSellingPlanForCard(
            card
          );
        if (!selectedPlan) {
          subscriptionDetails.hidden =
            true;
          return;
        }
        subscriptionDetails.hidden =
          false;
        updateEnhancedBenefitCopy();
        const lockedBenefit =
          subscriptionDetails.querySelector(
            '.kqv__subscription-benefit--locked'
          );
        if (lockedBenefit) {
          lockedBenefit.hidden =
            selectedPlan.deliveryCount <= 1 &&
            getPlanIntervalMonths(
              selectedPlan
            ) <= 1;
        }
        renderPlanOptions(
          card,
          selectedPlan
        );
        if (subscriptionPerServing) {
          subscriptionPerServing.textContent =
            `${selectedPlan.perServingPriceFormatted} ${subscriptionCopy.perServingSuffix}`;
        }
        if (
          subscriptionDiscountBenefit
        ) {
          const discount =
            Math.round(
              getPlanDiscount(
                card,
                selectedPlan
              )
            );
          subscriptionDiscountBenefit
            .textContent =
              subscriptionCopy
                .discountEveryRenewal(
                  discount
                );
        }
      };
      const updatePerServingPrices =
        () => {
          cards.forEach((card) => {
            const priceElement =
              card.querySelector(
                '[data-kqv-per-serving-price]'
              );
            if (!priceElement) {
              return;
            }
            /*
             * New quantity UX keeps the pack cards
             * anchored to their one-time per-serving price.
             * Gel keeps its historical behaviour.
             */
            if (useEnhancedSubscriptionUI) {
              priceElement.textContent =
                card.dataset
                  .perServingPriceFormatted ||
                '';
              return;
            }
            const useSubscriptionPrice =
              activePurchaseType ===
                'subscription' &&
              isSubscriptionAvailableForCard(
                card
              );
            priceElement.textContent =
              useSubscriptionPrice
                ? card.dataset
                    .subscriptionPerServingPriceFormatted ||
                  card.dataset
                    .perServingPriceFormatted ||
                  ''
                : card.dataset
                    .perServingPriceFormatted ||
                  '';
          });
        };
      const updatePurchaseOptions = (
        card
      ) => {
        if (
          !hasSubscription ||
          !purchaseOptions.length ||
          !card
        ) {
          if (subscriptionDetails) {
            subscriptionDetails.hidden =
              true;
          }
          return;
        }
        const subscriptionAvailable =
          isSubscriptionAvailableForCard(
            card
          );
        if (
          activePurchaseType ===
            'subscription' &&
          !subscriptionAvailable
        ) {
          activePurchaseType =
            'one-time';
        }
        if (
          activePurchaseType ===
            'subscription' &&
          subscriptionAvailable
        ) {
          ensureActiveSellingPlanForCard(
            card
          );
        }
        picker.dataset
          .kqvPurchaseType =
          activePurchaseType;
        purchaseOptions.forEach(
          (option) => {
            const purchaseType =
              option.dataset
                .purchaseType;
            const isSelected =
              purchaseType ===
              activePurchaseType;
            const isSubscriptionOption =
              purchaseType ===
              'subscription';
            const isDisabled =
              isSubscriptionOption &&
              !subscriptionAvailable;
            if (
              isSubscriptionOption
            ) {
              /*
               * New quantity UX: keep Subscribe & save visible
               * on the 1-box state, but disabled with helper copy.
               * Legacy / Gel behaviour stays unchanged.
               */
              const shouldKeepVisible =
                useEnhancedSubscriptionUI;
              option.hidden =
                shouldKeepVisible
                  ? false
                  : !subscriptionAvailable;
              option.style.display =
                shouldKeepVisible ||
                subscriptionAvailable
                  ? ''
                  : 'none';
              if (
                shouldKeepVisible ||
                subscriptionAvailable
              ) {
                option.removeAttribute(
                  'aria-hidden'
                );
                option.removeAttribute(
                  'tabindex'
                );
              } else {
                option.setAttribute(
                  'aria-hidden',
                  'true'
                );
                option.setAttribute(
                  'tabindex',
                  '-1'
                );
              }
            }
            option.classList.toggle(
              'is-selected',
              isSelected
            );
            option.classList.toggle(
              'is-disabled',
              isDisabled
            );
            option.setAttribute(
              'aria-checked',
              isSelected
                ? 'true'
                : 'false'
            );
            option.setAttribute(
              'aria-disabled',
              isDisabled
                ? 'true'
                : 'false'
            );
            option.disabled =
              isDisabled;
          }
        );
        const oneTimePrice =
          picker.querySelector(
            '[data-kqv-one-time-price]'
          );
        const subscriptionPrice =
          picker.querySelector(
            '[data-kqv-subscription-price]'
          );
        const subscriptionCompare =
          picker.querySelector(
            '[data-kqv-subscription-compare]'
          );
        const subscriptionMeta =
          picker.querySelector(
            '[data-kqv-subscription-meta]'
          );
        if (oneTimePrice) {
          oneTimePrice.textContent =
            card.dataset
              .priceFormatted || '';
        }
        if (
          useEnhancedSubscriptionUI
        ) {
          if (subscriptionAvailable) {
            const displayPlan =
              getActiveSellingPlanForCard(
                card
              );
            const compare =
              getPlanComparePrice(
                card,
                displayPlan
              );
            if (
              subscriptionPrice &&
              displayPlan
            ) {
              subscriptionPrice.innerHTML =
                `${displayPlan.deliveryPriceFormatted} <span class="kqv__purchase-price-suffix">${getPlanPriceSuffix(
                  displayPlan
                )}</span>`;
            }
            if (subscriptionCompare) {
              subscriptionCompare.innerHTML =
                compare.formatted
                  ? `${compare.formatted} <span class="kqv__purchase-compare-suffix">${getPlanPriceSuffix(
                      displayPlan
                    )}</span>`
                  : '';
              subscriptionCompare.hidden =
                !compare.formatted;
            }
            if (subscriptionMeta) {
              subscriptionMeta.textContent =
                '';
              subscriptionMeta.hidden =
                true;
            }
            updateSubscriptionDetails(
              card
            );
          } else {
            /* 1-box disabled state from the approved mockup. */
            if (subscriptionPrice) {
              subscriptionPrice.textContent =
                '';
            }
            if (subscriptionCompare) {
              subscriptionCompare.textContent =
                '';
              subscriptionCompare.hidden =
                true;
            }
            if (subscriptionMeta) {
              subscriptionMeta.hidden =
                false;
              subscriptionMeta.textContent =
                subscriptionCopy
                  .unavailableSubscription;
            }
            if (subscriptionDetails) {
              subscriptionDetails.hidden =
                true;
            }
          }
        } else {
          if (subscriptionPrice) {
            subscriptionPrice.textContent =
              card.dataset
                .subscriptionPriceFormatted ||
              card.dataset
                .priceFormatted ||
              '';
          }
          if (subscriptionCompare) {
            subscriptionCompare.textContent =
              card.dataset
                .priceFormatted || '';
            subscriptionCompare.hidden =
              !subscriptionAvailable;
          }
          if (subscriptionMeta) {
            subscriptionMeta.hidden =
              false;
            const subscriptionDiscount =
              getSubscriptionDiscount(
                card
              );
            subscriptionMeta.textContent =
              subscriptionAvailable &&
              subscriptionDiscount > 0
                ? `-${formatPercent(
                    subscriptionDiscount
                  )}% on every order + Free Kaiju bottle`
                : 'Free Kaiju bottle';
          }
          if (subscriptionDetails) {
            subscriptionDetails.hidden =
              true;
          }
        }
        syncSellingPlanInput(card);
      };
      const updateAtc = (
        card
      ) => {
        if (!card) return;
        const pricing =
          getPricingForCard(card);
        const isAvailable =
          card.dataset.available ===
          'true';
        /*
         * Le bloc restock existe uniquement
         * sur le picker Gel.
         */
        const restockBlock =
          picker.querySelector(
            '[data-kqv-restock]'
          );
        const purchaseBlock =
          picker.querySelector(
            '[data-kqv-purchase-block]'
          );
        const showRestock =
          Boolean(restockBlock) &&
          !isAvailable;
        if (restockBlock) {
          restockBlock.hidden =
            !showRestock;
          restockBlock.setAttribute(
            'aria-hidden',
            showRestock
              ? 'false'
              : 'true'
          );
        }
        /*
         * Sur le Gel OOS, on masque tout le
         * bloc One Time / Subscription.
         */
        if (
          purchaseBlock &&
          restockBlock
        ) {
          purchaseBlock.hidden =
            showRestock;
          purchaseBlock.setAttribute(
            'aria-hidden',
            showRestock
              ? 'true'
              : 'false'
          );
        }
        const submitButton =
          getSubmitButton();
        if (submitButton) {
          /*
           * Pour le Gel OOS, on masque
           * complètement l'ATC.
           *
           * Sur les autres produits, le
           * comportement reste inchangé.
           */
          if (restockBlock) {
            submitButton.hidden =
              showRestock;
          }
          submitButton.disabled =
            !isAvailable;
          submitButton.setAttribute(
            'aria-disabled',
            isAvailable
              ? 'false'
              : 'true'
          );
        }
        /*
         * Sécurité supplémentaire si le thème
         * affiche un bouton de paiement dynamique.
         */
        const dynamicCheckout =
          getDynamicCheckout();
        if (
          dynamicCheckout &&
          restockBlock
        ) {
          dynamicCheckout.hidden =
            showRestock;
        }
        const atcLabel =
          getAtcLabel();
        if (atcLabel) {
          if (isAvailable) {
            const enhancedSubscription =
              pricing.isSubscription &&
              useEnhancedSubscriptionUI;
            const activePlan =
              enhancedSubscription
                ? getActiveSellingPlanForCard(
                    card
                  )
                : null;
            const showCompare =
              !enhancedSubscription &&
              pricing.discount > 0 &&
              pricing.referencePrice >
                0 &&
              pricing
                .referencePriceFormatted;
            const atcPriceFormatted =
              pricing.priceFormatted;
            const termSuffix = activePlan
              ? activePlan.deliveryCount > 1
                ? ` ${subscriptionCopy.forMonths(
                    activePlan.deliveryCount
                  )}`
                : getPlanIntervalMonths(
                    activePlan
                  ) > 1
                  ? ` ${subscriptionCopy.forMonths(
                      getPlanIntervalMonths(
                        activePlan
                      )
                    )}`
                  : ` ${subscriptionCopy.monthSuffix}`
              : '';
            atcLabel.innerHTML = `
              <span class="qty-test-atc__main">
                ${addToCartLabel} · <span class="qty-test-atc__price">${atcPriceFormatted}</span><span class="qty-test-atc__period">${termSuffix}</span>
              </span>
              <span class="qty-test-atc__compare"${
                showCompare
                  ? ''
                  : ' hidden'
              }>
                ${
                  showCompare
                    ? pricing.referencePriceFormatted
                    : ''
                }
              </span>
            `;
          } else {
            atcLabel.textContent =
              soldOutLabel;
          }
        }
        const atcDiscount =
          getAtcDiscount();
        if (atcDiscount) {
          const hideForEnhancedSubscription =
            pricing.isSubscription &&
            useEnhancedSubscriptionUI;
          if (
            isAvailable &&
            pricing.discount > 0 &&
            !hideForEnhancedSubscription
          ) {
            atcDiscount.textContent =
              `-${pricing.discountFormatted}%`;
            atcDiscount.hidden =
              false;
          } else {
            atcDiscount.textContent =
              '';
            atcDiscount.hidden =
              true;
          }
        }
      };
      const scheduleAtcSync = (
        card
      ) => {
        if (!card) return;
        requestAnimationFrame(() => {
          if (!picker.isConnected) {
            return;
          }
          updateAtc(card);
        });
        window.setTimeout(() => {
          if (!picker.isConnected) {
            return;
          }
          updateAtc(card);
        }, 80);
      };
      const updateUI = (
        card,
        options = {}
      ) => {
        const allowDisabled =
          options.allowDisabled ||
          false;
        const syncVariant =
          options.syncVariant !==
          false;
        if (!card) return;
        const currentRadio =
          card.querySelector(
            'input[type="radio"]'
          );
        if (!allowDisabled) {
          if (card.hidden) return;
          if (
            currentRadio?.disabled
          ) {
            return;
          }
          if (
            card.dataset.available !==
            'true'
          ) {
            return;
          }
        }
        cards.forEach((item) => {
          item.classList.remove(
            'is-selected'
          );
          const radio =
            item.querySelector(
              'input[type="radio"]'
            );
          if (radio) {
            radio.checked = false;
          }
        });
        card.classList.add(
          'is-selected'
        );
        if (currentRadio) {
          currentRadio.checked =
            true;
        }
        if (
          activePurchaseType ===
            'subscription' &&
          !isSubscriptionAvailableForCard(
            card
          )
        ) {
          activePurchaseType =
            'one-time';
        }
        if (syncVariant) {
          const variantId =
            card.dataset.variantId;
          getVariantInputs().forEach(
            (input) => {
              input.value =
                variantId;
              input.disabled = false;
              input.dispatchEvent(
                new Event('change', {
                  bubbles: true,
                })
              );
            }
          );
        }
        updatePerServingPrices();
        updatePurchaseOptions(card);
        updateAtc(card);
        scheduleAtcSync(card);
      };
      /* =====================================================
         PURCHASE OPTIONS
         ===================================================== */
      const initPurchaseOptions =
        () => {
          if (
            !hasSubscription ||
            !purchaseOptions.length
          ) {
            return;
          }
          purchaseOptions.forEach(
            (option) => {
              option.addEventListener(
                'click',
                (event) => {
                  event.preventDefault();
                  const selectedCard =
                    getSelectedCard();
                  if (!selectedCard) {
                    return;
                  }
                  const purchaseType =
                    option.dataset
                      .purchaseType;
                  if (
                    purchaseType ===
                      'subscription'
                  ) {
                    if (
                      !isSubscriptionAvailableForCard(
                        selectedCard
                      )
                    ) {
                      return;
                    }
                    const selectedPlan =
                      ensureActiveSellingPlanForCard(
                        selectedCard
                      );
                    if (!selectedPlan) {
                      return;
                    }
                    activePurchaseType =
                      'subscription';
                  } else {
                    activePurchaseType =
                      'one-time';
                  }
                  updateUI(
                    selectedCard,
                    {
                      allowDisabled:
                        true,
                      syncVariant:
                        false,
                    }
                  );
                }
              );
            }
          );
        };
      /* =====================================================
         SIMPLE QUANTITY
         ===================================================== */
      const initSimpleQuantityPicker =
        () => {
          cards.forEach((card) => {
            card.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                const radio =
                  card.querySelector(
                    'input[type="radio"]'
                  );
                if (
                  radio?.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }
                updateUI(card);
              }
            );
            const radio =
              card.querySelector(
                'input[type="radio"]'
              );
            radio?.addEventListener(
              'change',
              () => {
                if (
                  !radio.checked ||
                  radio.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }
                updateUI(card);
              }
            );
          });
          const selectedCard =
            picker.querySelector(
              '.kqv__card.is-selected:not([hidden])'
            ) ||
            cards.find(
              (card) =>
                card.dataset
                  .available ===
                'true'
            ) ||
            cards[0];
          if (selectedCard) {
            updateUI(selectedCard, {
              allowDisabled:
                selectedCard.dataset
                  .available !==
                'true',
            });
          }
        };
      /* =====================================================
         SIZE + QUANTITY
         ===================================================== */
      const initSizeQuantityPicker =
        () => {
          const sizeCards =
            Array.from(
              picker.querySelectorAll(
                '[data-kqv-size-card]'
              )
            );
          const sizeRadios =
            Array.from(
              picker.querySelectorAll(
                '[data-kqv-size-radio]'
              )
            );
          const sizeBadge =
            picker.querySelector(
              '[data-kqv-size-badge]'
            );
          if (
            !sizeCards.length ||
            !sizeRadios.length
          ) {
            initSimpleQuantityPicker();
            return;
          }
          const getSelectedQuantityCard =
            () => {
              return (
                cards.find((card) =>
                  card.classList.contains(
                    'is-selected'
                  )
                ) || null
              );
            };
          const setSize = (
            sizeValue,
            preferredPackKey = '12'
          ) => {
            sizeCards.forEach(
              (sizeCard) => {
                const isSelected =
                  sizeCard.dataset
                    .sizeValue ===
                  sizeValue;
                const radio =
                  sizeCard.querySelector(
                    '[data-kqv-size-radio]'
                  );
                sizeCard.classList.toggle(
                  'is-selected',
                  isSelected
                );
                if (radio) {
                  radio.checked =
                    isSelected;
                }
              }
            );
            const selectedSizeCard =
              sizeCards.find(
                (sizeCard) =>
                  sizeCard.dataset
                    .sizeValue ===
                  sizeValue
              );
            if (
              sizeBadge &&
              selectedSizeCard
            ) {
              sizeBadge.textContent =
                `${selectedSizeCard.dataset.sizeLabel} per gel`;
            }
            const visibleCards =
              cards.filter(
                (card) =>
                  card.dataset
                    .sizeValue ===
                  sizeValue
              );
            cards.forEach((card) => {
              const shouldShow =
                card.dataset
                  .sizeValue ===
                sizeValue;
              const radio =
                card.querySelector(
                  'input[type="radio"]'
                );
              card.hidden =
                !shouldShow;
              if (radio) {
                radio.disabled =
                  !shouldShow ||
                  card.dataset
                    .available !==
                    'true';
                radio.checked =
                  false;
              }
              if (!shouldShow) {
                card.classList.remove(
                  'is-selected'
                );
              }
            });
            const nextCard =
              visibleCards.find(
                (card) =>
                  card.dataset
                    .packKey ===
                    preferredPackKey &&
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards.find(
                (card) =>
                  card.dataset
                    .packKey ===
                    '12' &&
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards.find(
                (card) =>
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards[0];
            if (nextCard) {
              updateUI(nextCard, {
                allowDisabled:
                  nextCard.dataset
                    .available !==
                    'true',
              });
            }
          };
          sizeCards.forEach(
            (sizeCard) => {
              sizeCard.addEventListener(
                'click',
                (event) => {
                  event.preventDefault();
                  const radio =
                    sizeCard.querySelector(
                      '[data-kqv-size-radio]'
                    );
                  if (!radio) return;
                  const currentCard =
                    getSelectedQuantityCard();
                  setSize(
                    radio.value,
                    currentCard?.dataset
                      .packKey || '12'
                  );
                }
              );
            }
          );
          sizeRadios.forEach(
            (radio) => {
              radio.addEventListener(
                'change',
                () => {
                  if (
                    !radio.checked
                  ) {
                    return;
                  }
                  const currentCard =
                    getSelectedQuantityCard();
                  setSize(
                    radio.value,
                    currentCard?.dataset
                      .packKey || '12'
                  );
                }
              );
            }
          );
          cards.forEach((card) => {
            card.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                const radio =
                  card.querySelector(
                    'input[type="radio"]'
                  );
                if (
                  card.hidden ||
                  radio?.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }
                updateUI(card);
              }
            );
          });
          const checkedSizeRadio =
            sizeRadios.find(
              (radio) =>
                radio.checked
            ) || sizeRadios[0];
          const selectedCard =
            getSelectedQuantityCard();
          if (checkedSizeRadio) {
            setSize(
              checkedSizeRadio.value,
              selectedCard?.dataset
                .packKey || '12'
            );
          }
        };
      initPurchaseOptions();
      if (isSizeQuantityMode) {
        initSizeQuantityPicker();
      } else {
        initSimpleQuantityPicker();
      }
    });
  }
  function bootKaijuQuantityVariantPickers() {
    initKaijuQuantityVariantPickers(
      document
    );
  }
  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      bootKaijuQuantityVariantPickers
    );
  } else {
    bootKaijuQuantityVariantPickers();
  }
  window.addEventListener(
    'load',
    bootKaijuQuantityVariantPickers
  );
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initKaijuQuantityVariantPickers(
        event.target
      );
    }
  );
  document.addEventListener(
    'shopify:section:select',
    (event) => {
      initKaijuQuantityVariantPickers(
        event.target
      );
    }
  );
  setTimeout(
    bootKaijuQuantityVariantPickers,
    300
  );
  setTimeout(
    bootKaijuQuantityVariantPickers,
    1000
  );
})();
